import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { platform } from '@platform/contracts';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  EmptyState,
  FormControlLabel,
  FormGroup,
  InventoryIcon,
  MONO,
  OverflowMenu,
  Page,
  PageHeader,
  Select,
  Stack,
  TextField,
  Typography,
  useToast,
  type Column,
} from '@platform/design-system';
import { describeError, fieldErrors, useCanOpen } from '@platform/app-kit';
import {
  usePackages,
  useProducts,
  useRetirePackage,
  useSavePackage,
  useTenants,
} from '../api/queries.ts';
import {
  MODEL_LABEL,
  dollars,
  dollarsInput,
  featureList,
  formatDate,
  microFromDollars,
} from '../shell/format.ts';
import {
  PACKAGE_CREATE_ROUTE,
  PACKAGE_RETIRE_ROUTE,
  PACKAGE_WRITE_ROUTE,
} from '../shell/routes.ts';

type Package = platform.Package;

/** The closed feature list (contract `FeatureKey`; the platform lane owns additions). */
const FEATURES: { key: string; label: string }[] = [
  { key: 'crm', label: 'CRM' },
  { key: 'forms', label: 'Forms' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'assistant', label: 'Assistant' },
  { key: 'sms', label: 'SMS (after carrier approval)' },
];

const schema = z.object({
  packageId: z.string().regex(/^pkg_[a-z0-9-]+$/, 'Ids look like pkg_team'),
  product: z.string().min(1, 'Choose the product this package is sold for'),
  name: z.string().trim().min(2, 'Give the package a name'),
  stripePriceId: z.string().regex(/^price_[A-Za-z0-9]+$/, 'A Stripe price id looks like price_…'),
  pool: z.boolean(),
  silo: z.boolean(),
  features: z.array(z.string()),
  // Assistant limits (#16), in dollars as typed; required when the Assistant feature is on.
  aiModels: z.array(z.enum(['fast', 'standard'])),
  aiMonthly: z.string(),
  aiDaily: z.string(),
  aiMember: z.string(),
});
type PackageForm = z.infer<typeof schema>;

const MODELS: { key: platform.AssistantModel; hint: string }[] = [
  { key: 'fast', hint: 'short, cheap answers' },
  { key: 'standard', hint: 'everyday work; the most capable model available' },
];

/** The allowance the form describes, or why it cannot be saved (field → message). */
function allowanceFromForm(
  values: PackageForm,
):
  | { ok: true; allowance?: platform.AssistantAllowance }
  | { ok: false; errors: Partial<Record<keyof PackageForm, string>> } {
  if (!values.features.includes('assistant')) return { ok: true };
  const errors: Partial<Record<keyof PackageForm, string>> = {};
  if (values.aiModels.length === 0) errors.aiModels = 'Allow at least one model';
  const monthly = microFromDollars(values.aiMonthly);
  if (monthly === undefined) errors.aiMonthly = 'A monthly limit in dollars, such as 20 or 7.50';
  const optional = (text: string, field: 'aiDaily' | 'aiMember') => {
    if (text.trim() === '') return undefined;
    const micro = microFromDollars(text);
    if (micro === undefined) errors[field] = 'A dollar amount, or leave it empty for no limit';
    return micro;
  };
  const daily = optional(values.aiDaily, 'aiDaily');
  const member = optional(values.aiMember, 'aiMember');
  if (Object.keys(errors).length > 0 || monthly === undefined) return { ok: false, errors };
  return {
    ok: true,
    allowance: {
      models: [...values.aiModels],
      monthlyLimitMicroUsd: monthly,
      ...(daily !== undefined ? { dailyLimitMicroUsd: daily } : {}),
      ...(member !== undefined ? { memberMonthlyLimitMicroUsd: member } : {}),
    },
  };
}

function allowanceSummary(a: platform.AssistantAllowance | undefined): string {
  if (!a) return 'Not included';
  return [
    `${dollars(a.monthlyLimitMicroUsd)} a month`,
    ...(a.dailyLimitMicroUsd !== undefined ? [`${dollars(a.dailyLimitMicroUsd)} a day`] : []),
  ].join(' · ');
}

/**
 * `/packages` — the catalog. Saving a package publishes a new version (`Packages_update` bumps
 * `version` server-side); tenants stay on the snapshot they were given until someone applies
 * the new version from the tenant page. Retiring stops new adoptions; snapshots are untouched.
 */
export function PackagesPage() {
  const packages = usePackages(true);
  const products = useProducts();
  const productName = (name: string) =>
    products.data?.find((p) => p.name === name)?.displayName ?? name;
  const tenants = useTenants();
  const save = useSavePackage();
  const retire = useRetirePackage();
  const toast = useToast();
  const canWrite = useCanOpen(PACKAGE_WRITE_ROUTE);
  const canCreate = useCanOpen(PACKAGE_CREATE_ROUTE);
  const canRetire = useCanOpen(PACKAGE_RETIRE_ROUTE);
  const [editing, setEditing] = useState<Package | null | 'new'>(null);
  const [retiring, setRetiring] = useState<Package | null>(null);

  const tenantsOn = (pkg: Package) =>
    tenants.data?.filter((t) => t.entitlement?.packageId === pkg.packageId) ?? [];
  const behind = (pkg: Package) =>
    tenantsOn(pkg).filter((t) => (t.entitlement?.packageVersion ?? 0) < pkg.version).length;

  const columns: Column<Package>[] = [
    {
      id: 'name',
      header: 'Package',
      sortValue: (p) => p.name,
      cell: (p) => (
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {p.name}
            </Typography>
            {p.retired && <Badge label="Retired" />}
          </Stack>
          <Typography variant="caption" color="text.tertiary" sx={MONO}>
            {p.packageId}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'product',
      header: 'Product',
      sortValue: (p) => productName(p.product),
      cell: (p) => productName(p.product),
    },
    {
      id: 'version',
      header: 'Version',
      numeric: true,
      sortValue: (p) => p.version,
      cell: (p) => `v${p.version}`,
    },
    {
      id: 'tiers',
      header: 'Tiers',
      cell: (p) => p.tiers.map((t) => (t === 'pool' ? 'Pool' : 'Dedicated')).join(', '),
    },
    { id: 'features', header: 'Features', cell: (p) => featureList(p.features) },
    {
      id: 'assistant',
      header: 'Assistant limits',
      sortValue: (p) => p.assistant?.monthlyLimitMicroUsd ?? -1,
      cell: (p) => allowanceSummary(p.assistant),
    },
    {
      id: 'price',
      header: 'Price',
      cell: (p) => (
        <Typography variant="caption" sx={MONO}>
          {p.stripePriceId}
        </Typography>
      ),
    },
    {
      id: 'tenants',
      header: 'Tenants',
      numeric: true,
      sortValue: (p) => tenantsOn(p).length,
      cell: (p) => {
        const n = tenantsOn(p).length;
        const b = behind(p);
        return b > 0 ? `${n} (${b} on older versions)` : String(n);
      },
    },
    {
      id: 'updated',
      header: 'Updated',
      sortValue: (p) => new Date(p.updatedAt),
      cell: (p) => formatDate(p.updatedAt),
    },
    ...(canWrite || canRetire
      ? [
          {
            id: 'actions',
            header: '',
            actions: true,
            cell: (p: Package) => (
              <OverflowMenu
                label={`Actions for ${p.name}`}
                items={[
                  ...(canWrite
                    ? [{ id: 'edit', label: 'Edit (new version)', onSelect: () => setEditing(p) }]
                    : []),
                  ...(canRetire && !p.retired
                    ? [
                        {
                          id: 'retire',
                          label: 'Retire package',
                          destructive: true,
                          onSelect: () => setRetiring(p),
                        },
                      ]
                    : []),
                ]}
              />
            ),
          } satisfies Column<Package>,
        ]
      : []),
  ];

  return (
    <Page>
      <PageHeader
        title="Packages"
        meta="Editing a package publishes a new version; existing tenant snapshots never change"
        actions={
          canCreate ? (
            <Button variant="contained" onClick={() => setEditing('new')}>
              New package
            </Button>
          ) : undefined
        }
      />
      {packages.isError && (
        <Alert severity="error">
          {describeError(packages.error, 'Could not load the catalog')}
        </Alert>
      )}
      <DataTable
        label="Packages"
        columns={columns}
        rows={packages.data ?? []}
        getRowId={(p) => p.packageId}
        loading={packages.isLoading}
        defaultSort={{ columnId: 'name', direction: 'asc' }}
        {...(canWrite ? { onRowClick: (p: Package) => setEditing(p) } : {})}
        emptyState={
          <EmptyState
            plain
            icon={<InventoryIcon />}
            title="No packages yet"
            description="Create the first package tenants can be onboarded onto."
            action={
              <Button variant="contained" onClick={() => setEditing('new')} disabled={!canCreate}>
                New package
              </Button>
            }
          />
        }
      />
      {editing !== null && (
        <PackageDialog
          pkg={editing === 'new' ? null : editing}
          products={products.data ?? []}
          tenantsOnPackage={editing === 'new' ? 0 : tenantsOn(editing).length}
          busy={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(values, assistant) => {
            const tiers: platform.Tier[] = [
              ...(values.pool ? (['pool'] as const) : []),
              ...(values.silo ? (['silo'] as const) : []),
            ];
            const input =
              editing === 'new'
                ? {
                    kind: 'create' as const,
                    body: {
                      packageId: values.packageId,
                      product: values.product,
                      name: values.name,
                      tiers,
                      features: values.features,
                      stripePriceId: values.stripePriceId,
                      ...(assistant ? { assistant } : {}),
                    },
                  }
                : {
                    kind: 'update' as const,
                    packageId: editing.packageId,
                    body: {
                      name: values.name,
                      tiers,
                      features: values.features,
                      stripePriceId: values.stripePriceId,
                      // Taking the assistant out of a package that had it sends null.
                      ...(assistant ? { assistant } : editing.assistant ? { assistant: null } : {}),
                    },
                  };
            save.mutate(input, {
              onSuccess: (saved) => {
                setEditing(null);
                const n = editing === 'new' ? 0 : tenantsOn(saved).length;
                toast.success(
                  editing === 'new'
                    ? `${saved.name} created as v1`
                    : `${saved.name} saved as v${saved.version}${n ? ` · ${n} ${n === 1 ? 'tenant stays' : 'tenants stay'} on their snapshots` : ''}`,
                );
              },
              onError: (e) => toast.error(describeError(e, 'Could not save the package')),
            });
          }}
        />
      )}
      <ConfirmDialog
        open={Boolean(retiring)}
        title={retiring ? `Retire ${retiring.name}?` : 'Retire?'}
        message="No tenant can adopt it from now on. Tenants already on it keep their snapshots."
        confirmLabel="Retire"
        destructive
        busy={retire.isPending}
        onClose={() => setRetiring(null)}
        onConfirm={() => {
          const pkg = retiring;
          if (!pkg) return;
          retire.mutate(pkg.packageId, {
            onSuccess: () => {
              setRetiring(null);
              toast.success(`${pkg.name} retired`);
            },
            onError: (e) => toast.error(describeError(e, 'Could not retire the package')),
          });
        }}
      />
    </Page>
  );
}

function toForm(pkg: Package | null): PackageForm {
  return pkg
    ? {
        packageId: pkg.packageId,
        product: pkg.product,
        name: pkg.name,
        stripePriceId: pkg.stripePriceId,
        pool: pkg.tiers.includes('pool'),
        silo: pkg.tiers.includes('silo'),
        features: [...pkg.features],
        aiModels: [...(pkg.assistant?.models ?? ['fast', 'standard'])],
        aiMonthly: dollarsInput(pkg.assistant?.monthlyLimitMicroUsd),
        aiDaily: dollarsInput(pkg.assistant?.dailyLimitMicroUsd),
        aiMember: dollarsInput(pkg.assistant?.memberMonthlyLimitMicroUsd),
      }
    : {
        packageId: 'pkg_',
        product: '',
        name: '',
        stripePriceId: 'price_',
        pool: true,
        silo: false,
        features: ['crm'],
        aiModels: ['fast', 'standard'],
        aiMonthly: '',
        aiDaily: '',
        aiMember: '',
      };
}

function PackageDialog({
  pkg,
  products,
  tenantsOnPackage,
  busy,
  onClose,
  onSave,
}: {
  pkg: Package | null;
  products: readonly platform.Product[];
  tenantsOnPackage: number;
  busy: boolean;
  onClose: () => void;
  onSave: (values: PackageForm, assistant: platform.AssistantAllowance | undefined) => void;
}) {
  const form = useForm<PackageForm>({ resolver: zodResolver(schema), defaultValues: toForm(pkg) });
  useEffect(() => {
    form.reset(toForm(pkg));
  }, [pkg, form]);

  const submit = form.handleSubmit((values) => {
    if (!values.pool && !values.silo) {
      form.setError('pool', { message: 'Offer the package on at least one tier' });
      return;
    }
    const assistant = allowanceFromForm(values);
    if (!assistant.ok) {
      for (const [name, message] of Object.entries(assistant.errors))
        form.setError(name as keyof PackageForm, { message });
      return;
    }
    onSave(values, assistant.allowance);
  });
  const withAssistant = useWatch({ control: form.control, name: 'features' }).includes('assistant');
  const titleId = 'package-dialog-title';
  const applyFieldErrors = (e: unknown) => {
    for (const [name, message] of Object.entries(fieldErrors(e))) {
      if (name === 'packageId' || name === 'product' || name === 'name' || name === 'stripePriceId')
        form.setError(name, { message });
    }
  };

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby={titleId}
    >
      <form
        onSubmit={(e) => {
          void submit(e).catch(applyFieldErrors);
        }}
        noValidate
      >
        <DialogTitle id={titleId}>{pkg ? `Edit ${pkg.name}` : 'New package'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {pkg && (
              <Alert severity="info">
                Saving publishes v{pkg.version + 1}.{' '}
                {tenantsOnPackage > 0
                  ? `${tenantsOnPackage} ${tenantsOnPackage === 1 ? 'tenant keeps its' : 'tenants keep their'} current snapshot until applied.`
                  : 'No tenant is on this package yet.'}
              </Alert>
            )}
            <TextField
              label="Package id"
              disabled={Boolean(pkg)}
              {...form.register('packageId')}
              error={form.formState.errors.packageId?.message}
              hint={
                pkg ? 'Ids never change' : 'pkg_ followed by lower-case letters, digits and dashes'
              }
            />
            <Controller
              control={form.control}
              name="product"
              render={({ field }) => (
                <Select
                  label="Product"
                  disabled={Boolean(pkg)}
                  options={products.map((p) => ({ value: p.name, label: p.displayName }))}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Choose a product"
                  error={form.formState.errors.product?.message}
                  hint={
                    pkg ? 'A package never changes product' : 'Tenants of this product can adopt it'
                  }
                />
              )}
            />
            <TextField
              label="Name"
              {...form.register('name')}
              error={form.formState.errors.name?.message}
            />
            <TextField
              label="Stripe price id"
              {...form.register('stripePriceId')}
              error={form.formState.errors.stripePriceId?.message}
              hint="From the Stripe dashboard; billing charges this price."
            />
            <FormGroup aria-label="Tiers">
              <Controller
                control={form.control}
                name="pool"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Pool tier (shared cell)"
                  />
                )}
              />
              <Controller
                control={form.control}
                name="silo"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Dedicated cell"
                  />
                )}
              />
              {form.formState.errors.pool?.message && (
                <Typography variant="caption" color="error">
                  {form.formState.errors.pool.message}
                </Typography>
              )}
            </FormGroup>
            <Controller
              control={form.control}
              name="features"
              render={({ field }) => (
                <FormGroup aria-label="Features">
                  {FEATURES.map((f) => (
                    <FormControlLabel
                      key={f.key}
                      control={
                        <Checkbox
                          checked={field.value.includes(f.key)}
                          onChange={(e) =>
                            field.onChange(
                              e.target.checked
                                ? [...field.value, f.key]
                                : field.value.filter((k) => k !== f.key),
                            )
                          }
                        />
                      }
                      label={f.label}
                    />
                  ))}
                </FormGroup>
              )}
            />
            {withAssistant && (
              <Stack spacing={2} role="group" aria-label="Assistant limits">
                <Typography variant="subtitle2">Assistant limits</Typography>
                <Typography variant="caption" color="text.secondary">
                  Checked before every call, per tenant: a call that could cross a limit is refused
                  and costs nothing. Callers are warned from 80%. Months and days are UTC.
                </Typography>
                <Controller
                  control={form.control}
                  name="aiModels"
                  render={({ field }) => (
                    <FormGroup aria-label="Models">
                      {MODELS.map((m) => (
                        <FormControlLabel
                          key={m.key}
                          control={
                            <Checkbox
                              checked={field.value.includes(m.key)}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.checked
                                    ? [...field.value, m.key]
                                    : field.value.filter((k) => k !== m.key),
                                )
                              }
                            />
                          }
                          label={`${MODEL_LABEL[m.key]} — ${m.hint}`}
                        />
                      ))}
                      {form.formState.errors.aiModels?.message && (
                        <Typography variant="caption" color="error">
                          {form.formState.errors.aiModels.message}
                        </Typography>
                      )}
                    </FormGroup>
                  )}
                />
                <TextField
                  label="Monthly limit per tenant (USD)"
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  {...form.register('aiMonthly')}
                  error={form.formState.errors.aiMonthly?.message}
                  hint="The hard stop for the whole tenant each month"
                />
                <TextField
                  label="Daily limit per tenant (USD)"
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  {...form.register('aiDaily')}
                  error={form.formState.errors.aiDaily?.message}
                  hint="Optional; empty means only the monthly limit"
                />
                <TextField
                  label="Monthly limit per person (USD)"
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  {...form.register('aiMember')}
                  error={form.formState.errors.aiMember?.message}
                  hint="Optional; empty means no per-person limit"
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" busy={busy} busyLabel="Saving…">
            {pkg ? `Save as v${pkg.version + 1}` : 'Create package'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
